import { useEffect, useId, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import { getCopyTargetCourseOptions } from '@/features/content/componentActions/courseOptionUtils';
import type {
  EditableQuestion,
  QuestionSectionKind,
  RawComponent,
} from '@/features/content/editor/editorTypes';
import { useCourse } from '@/features/courses/useCourse';
import { useCourses } from '@/features/courses/useCourses';

import { useCopyQuestion } from './useCopyQuestion';
import {
  getQuestionSection,
  toRawQuestions,
  toSectionArray,
} from './questionServiceUtils';
import type { CopyQuestionPosition } from './copyQuestionService';
import styles from './CopyQuestionDialog.module.css';

export interface CopyQuestionSuccess {
  message: string;
  savedComponent: RawComponent;
  targetComponentId: string;
  targetCourseId: string;
}

interface CopyQuestionDialogProps {
  currentComponent: RawComponent;
  /**
   * The chapter open on the page has changes not yet saved. A copy into it
   * goes to the server's copy, without them, and the page's next Save would
   * write them over the copy: so a copy into it waits until they are saved
   * or cancelled.
   */
  currentComponentHasUnsavedChanges?: boolean;
  currentComponentId: string;
  currentComponentTitle: string;
  currentCourseId: string;
  currentCourseTitle: string;
  onOpenChange(open: boolean): void;
  onSuccess(result: CopyQuestionSuccess): void | Promise<void>;
  open: boolean;
  questions: EditableQuestion[];
  scope: QuestionSectionKind;
}

interface TargetComponentOption {
  id: string;
  rawComponent: RawComponent;
  title: string;
}

interface PositionOption {
  label: string;
  value: string;
}

const UNSAVED_TARGET_MESSAGE =
  'Save or cancel your changes to this chapter first, then copy into it.';

function parsePositionValue(value: string): CopyQuestionPosition {
  if (value === 'end') {
    return { placement: 'end' as const };
  }

  const [placement, rawIndex] = value.split(':');
  const questionIndex = Number(rawIndex);

  if (
    (placement !== 'before' && placement !== 'after') ||
    !Number.isFinite(questionIndex)
  ) {
    return { placement: 'end' as const };
  }

  return {
    placement: placement === 'before' ? 'before' : 'after',
    questionIndex,
  };
}

function getScopeLabel(scope: QuestionSectionKind) {
  return scope === 'preLecture' ? 'pre-lecture' : 'homework';
}

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : 'Unable to copy questions right now.';
}

function normalizeBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isChapterCopyTarget(
  component: {
    isComplete?: boolean;
    isLive?: boolean;
    isPreLectureComplete?: boolean;
    isPreLectureLive?: boolean;
    rawComponent?: unknown;
  },
  scope: QuestionSectionKind,
) {
  const rawComponent = isRecord(component.rawComponent)
    ? component.rawComponent
    : {};

  if (scope === 'homework') {
    const isLive =
      component.isLive ??
      normalizeBoolean(rawComponent.is_live ?? rawComponent.isLive) ??
      false;
    const isComplete =
      component.isComplete ??
      normalizeBoolean(rawComponent.is_complete ?? rawComponent.isComplete) ??
      false;

    return !isLive && !isComplete;
  }

  const isPreLectureLive =
    component.isPreLectureLive ??
    normalizeBoolean(
      rawComponent.is_pre_lecture_live ??
        rawComponent.isPreLectureLive,
    ) ??
    false;
  const isPreLectureComplete =
    component.isPreLectureComplete ??
    normalizeBoolean(
      rawComponent.is_pre_lecture_complete ??
        rawComponent.isPreLectureComplete,
    ) ??
    false;

  return !isPreLectureLive && !isPreLectureComplete;
}

export function CopyQuestionDialog({
  currentComponent,
  currentComponentHasUnsavedChanges = false,
  currentComponentId,
  currentComponentTitle,
  currentCourseId,
  currentCourseTitle,
  onOpenChange,
  onSuccess,
  open,
  questions,
  scope,
}: CopyQuestionDialogProps) {
  const courseSelectId = useId();
  const componentSelectId = useId();
  const positionSelectId = useId();
  const coursesQuery = useCourses();
  const copyQuestion = useCopyQuestion();
  const [selectedCourseId, setSelectedCourseId] = useState(currentCourseId);
  const [selectedComponentId, setSelectedComponentId] =
    useState(currentComponentId);
  const [selectedPositionValue, setSelectedPositionValue] = useState('end');
  const selectedCourseQuery = useCourse(selectedCourseId || undefined);
  const courses = coursesQuery.data ?? [];
  const userCourseIds =
    coursesQuery.data?.userCourseIds && coursesQuery.data.userCourseIds.length > 0
      ? coursesQuery.data.userCourseIds
      : courses.map((course) => course.id);
  const courseOptions = useMemo(
    () =>
      getCopyTargetCourseOptions({
        courses,
        currentCourseId,
        currentCourseTitle,
        userCourseIds,
      }),
    [courses, currentCourseId, currentCourseTitle, userCourseIds],
  );
  const selectedCourse = courseOptions.find(
    (option) => option.id === selectedCourseId,
  );
  const targetComponentOptions = useMemo<TargetComponentOption[]>(() => {
    const options =
      selectedCourseQuery.data?.components
        .flatMap((component) => {
          if (
            component.kind !== 'component' ||
            !component.rawComponent ||
            !isChapterCopyTarget(component, scope)
          ) {
            return [];
          }

          return [
            {
              id: component.componentId ?? component.id,
              rawComponent: component.rawComponent as unknown as RawComponent,
              title: component.title || 'Untitled chapter',
            },
          ];
        }) ?? [];

    if (selectedCourseId !== currentCourseId) {
      return options;
    }

    if (options.some((option) => option.id === currentComponentId)) {
      return options;
    }

    if (
      !isChapterCopyTarget(
        {
          rawComponent: currentComponent,
        },
        scope,
      )
    ) {
      return options;
    }

    return [
      {
        id: currentComponentId,
        rawComponent: currentComponent,
        title: currentComponentTitle || 'Untitled chapter',
      },
      ...options,
    ];
  }, [
    currentComponent,
    currentComponentId,
    currentComponentTitle,
    currentCourseId,
    scope,
    selectedCourseId,
    selectedCourseQuery.data?.components,
  ]);
  const targetComponent = targetComponentOptions.find(
    (option) => option.id === selectedComponentId,
  );
  const targetQuestions = useMemo(() => {
    if (!targetComponent) {
      return [];
    }

    const questionSection = toSectionArray(targetComponent.rawComponent.sections)
      .map((rawSection) => getQuestionSection(rawSection, scope))
      .find((candidate) => Boolean(candidate));

    return toRawQuestions(questionSection?.questions);
  }, [scope, targetComponent]);
  const positionOptions = useMemo<PositionOption[]>(() => {
    const questionPositionOptions = targetQuestions.map((_, index) => {
      const questionNumber = index + 1;

      return {
        label: `Before question ${questionNumber}`,
        value: `before:${index}`,
      };
    });

    return [
      {
        label: 'End of question set',
        value: 'end',
      },
      ...questionPositionOptions,
    ];
  }, [targetQuestions]);
  const scopeLabel = getScopeLabel(scope);
  const copyTitle =
    questions.length === 1
      ? `Copy ${scopeLabel} question`
      : `Copy ${questions.length} ${scopeLabel} questions`;

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedCourseId(currentCourseId);
    setSelectedComponentId(currentComponentId);
    setSelectedPositionValue('end');
  }, [currentComponentId, currentCourseId, open]);

  useEffect(() => {
    if (!open || targetComponentOptions.length === 0) {
      return;
    }

    if (
      !selectedComponentId ||
      !targetComponentOptions.some((option) => option.id === selectedComponentId)
    ) {
      setSelectedComponentId(targetComponentOptions[0]!.id);
    }
  }, [open, selectedComponentId, targetComponentOptions]);

  useEffect(() => {
    if (
      !open ||
      positionOptions.some((option) => option.value === selectedPositionValue)
    ) {
      return;
    }

    setSelectedPositionValue('end');
  }, [open, positionOptions, selectedPositionValue]);

  const isCopyIntoUnsavedChapter =
    currentComponentHasUnsavedChanges &&
    selectedCourseId === currentCourseId &&
    targetComponent?.id === currentComponentId;

  async function handleCopy() {
    if (!targetComponent || !selectedCourse || isCopyIntoUnsavedChapter) {
      return;
    }

    const savedComponent = await copyQuestion.mutateAsync({
      position: parsePositionValue(selectedPositionValue),
      questions,
      scope,
      targetComponent: targetComponent.rawComponent,
      targetComponentId: targetComponent.id,
      targetCourseId: selectedCourse.id,
    });

    await onSuccess({
      message:
        questions.length === 1
          ? `Copied to ${targetComponent.title}`
          : `Copied ${questions.length} questions to ${targetComponent.title}`,
      savedComponent,
      targetComponentId: targetComponent.id,
      targetCourseId: selectedCourse.id,
    });
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>{copyTitle}</Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Choose where to append the selected {scopeLabel} question
                  {questions.length === 1 ? '' : 's'}.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div className={styles.body}>
            <label className={styles.field} htmlFor={courseSelectId}>
              <span className={styles.label}>Copy to course</span>
              <select
                className={styles.select}
                disabled={coursesQuery.isPending || copyQuestion.isPending}
                id={courseSelectId}
                onChange={(event) => {
                  setSelectedCourseId(event.target.value);
                  setSelectedComponentId('');
                  setSelectedPositionValue('end');
                }}
                value={selectedCourseId}
              >
                {courseOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} · {option.id}
                    {option.isCurrent ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field} htmlFor={componentSelectId}>
              <span className={styles.label}>Copy to chapter</span>
              <select
                className={styles.select}
                disabled={
                  selectedCourseQuery.isPending ||
                  targetComponentOptions.length === 0 ||
                  copyQuestion.isPending
                }
                id={componentSelectId}
                onChange={(event) => {
                  setSelectedComponentId(event.target.value);
                  setSelectedPositionValue('end');
                }}
                value={selectedComponentId}
              >
                {selectedCourseQuery.isPending ? (
                  <option value="">Loading chapters...</option>
                ) : targetComponentOptions.length === 0 ? (
                  <option value="">No available chapters</option>
                ) : (
                  targetComponentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.title || 'Untitled chapter'}
                    </option>
                  ))
                )}
              </select>
            </label>

            <label className={styles.field} htmlFor={positionSelectId}>
              <span className={styles.label}>Copy position</span>
              <select
                className={styles.select}
                disabled={
                  !targetComponent ||
                  selectedCourseQuery.isPending ||
                  copyQuestion.isPending
                }
                id={positionSelectId}
                onChange={(event) => {
                  setSelectedPositionValue(event.target.value);
                }}
                value={selectedPositionValue}
              >
                {positionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {targetComponentOptions.length === 0 && !selectedCourseQuery.isPending ? (
              <p className={styles.helperText}>
                No available chapters in this course -- all chapters are
                already visible to students.
              </p>
            ) : (
              <p className={styles.helperText}>
                Question{questions.length === 1 ? '' : 's'} will be copied to
                the selected position in the {scopeLabel} question section.
              </p>
            )}

            {isCopyIntoUnsavedChapter ? (
              <p className={styles.helperText} role="status">
                {UNSAVED_TARGET_MESSAGE}
              </p>
            ) : null}

            {copyQuestion.isError ? (
              <p aria-live="polite" className={styles.error} role="alert">
                {getErrorMessage(copyQuestion.error)}
              </p>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button disabled={copyQuestion.isPending} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={
                !targetComponent ||
                questions.length === 0 ||
                isCopyIntoUnsavedChapter ||
                copyQuestion.isPending
              }
              onClick={() => {
                void handleCopy();
              }}
            >
              {copyQuestion.isPending ? 'Copying...' : 'Copy'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
